import React, { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuCalendar,
  LuCheck,
  LuCircleCheck,
  LuClipboardList,
  LuDownload,
  LuFilePlus,
  LuFileText,
  LuLock,
  LuMail,
  LuMessageCircle,
  LuPackage,
  LuPencil,
  LuUser,
} from "react-icons/lu";
import { PageLayout } from "../../app/PageLayout";
import { Button, Card, Dialog, SaveStatus, Skeleton, Spinner, cn } from "../../components/ui";
import { ShareCard } from "../../components/patterns/ShareCard";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { SurgeryRequest } from "../../types/pdf.types";
import { useAutoSave } from "../../hooks/useAutoSave";
import { usePdfActions } from "../../hooks/usePdfActions";
import { useStreamingRequest } from "./useStreamingRequest";
import { useReferences } from "../references/useReferences";
import { formatSurgeryRequest, transformDateAndHour } from "../../lib/formatters";
import { INTERNACAO_PLANS, OPME_PLANS } from "../../constants/healthPlans";
import { HealthPlanPicker, SummaryDescriptionDialog } from "../guides";
import { ReferencesPanel } from "../references/ReferencesPanel";
import { ReferencesReviewDialog } from "../references/ReferencesReviewDialog";
import { EmailComposer, WhatsAppComposer } from "../sharing";
import { t } from "../../i18n";
import { PATHS } from "../../app/paths";

type FieldKey = keyof SurgeryRequest;

const LONG_TEXT = 180;

/**
 * Campo do pedido: textarea que cresce com o texto, com lápis (editável) ou
 * cadeado (gerado). Texto longo fica recolhido com "Ver mais" e abre ao focar.
 */
const OrderField: React.FC<{ label: string; field: FieldKey; readOnly?: boolean }> = ({ label, field, readOnly }) => {
  const { surgeryRequestData, setSurgeryRequestData } = useOrderDraft();
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const value = surgeryRequestData[field];
  const collapsed = value.length > LONG_TEXT && !expanded;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-8 items-center justify-between gap-2.5">
        <label htmlFor={id} className="text-caption font-bold uppercase tracking-wide text-primary">
          {label}
        </label>
        {readOnly && <LuLock size={14} className="text-fg-subtle" aria-label={t("download.readOnly")} />}
      </div>
      <div className="relative">
        <textarea
          id={id}
          value={value}
          onChange={(e) => setSurgeryRequestData((prev) => ({ ...prev, [field]: e.target.value }))}
          readOnly={readOnly}
          rows={1}
          className={cn(
            // ponytail: sem field-sizing (Firefox) o campo não cresce sozinho; lá ele fica redimensionável.
            "min-h-[2.7em] w-full resize-y rounded-control border border-transparent bg-surface-muted px-3 py-2.5 text-body leading-relaxed outline-none transition-colors duration-fast [field-sizing:content] supports-[field-sizing:content]:resize-none",
            readOnly ? "text-fg-muted" : "pr-10 hover:border-primary-line focus:border-primary focus:bg-surface",
            collapsed && "max-h-[7.2em] overflow-hidden [mask-image:linear-gradient(#000_65%,transparent)] focus:max-h-none focus:[mask-image:none]"
          )}
        />
        {!readOnly && <LuPencil size={16} aria-hidden className="pointer-events-none absolute right-3 top-3 text-fg-subtle" />}
      </div>
      {value.length > LONG_TEXT && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-controls={id}
          aria-expanded={expanded}
          className="min-h-11 self-start rounded-control px-2 text-caption font-semibold text-primary hover:bg-primary-tint focus-visible:outline-none focus-visible:shadow-focus"
        >
          {expanded ? t("download.showLess") : t("download.showMore")}
        </button>
      )}
    </div>
  );
};

/** Texto do pedido chegando pelo streaming (###, -, **negrito**, ---). */
const StreamedOrder: React.FC<{ text: string }> = ({ text }) => (
  <Card>
    {/* Key por índice: só a linha que acabou de chegar anima. */}
    <div className="flex flex-col gap-2 text-body [&>*]:animate-fade-in">
      {text.split("\n").map((line, i) => {
        if (/^-{3,}\s*$/.test(line)) return <hr key={i} className="border-line" />;
        if (line.startsWith("###")) return <h3 key={i} className="text-heading font-semibold text-primary">{line.replace("###", "").trim()}</h3>;
        const chunks = line.replace(/^-\s*/, "").split("**").map((c, j) => (j % 2 ? <b key={j}>{c}</b> : c));
        return line.startsWith("-") ? <li key={i} className="ml-5">{chunks}</li> : <p key={i}>{chunks}</p>;
      })}
    </div>
  </Card>
);

export const DownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const context = useOrderDraft();
  const data = context.surgeryRequestData;
  const pdf = usePdfActions();
  const refs = useReferences();
  const order = useStreamingRequest();
  const { autoSave, status, savedAt } = useAutoSave(
    {
      timestamp: context.timestamp,
      hospital: data.hospital,
      resumoOPME: data.opmeSummary,
      pacient_name: data.patientName,
      dataDesejada: transformDateAndHour(data.desiredDateTime),
      pedido_cirurgia: formatSurgeryRequest(data),
      cids: data.cids,
      dataEmissao: data.issueDate,
    },
    [data]
  );

  const [approved, setApproved] = useState(false);
  const [otherGuides, setOtherGuides] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [whatsAppOpen, setWhatsAppOpen] = useState(false);
  const ready = order.status === "ready";
  const approvedRefs = context.lastApprovedRefs ?? [];

  // Voltar mantém o pedido gerado: a IA só roda de novo se o médico mudar as entradas.
  const handleBack = () => {
    context.setLastApprovedRefs(undefined);
    refs.resetState();
    pdf.setShowReferencesModal(false);
    navigate(PATHS.surgicalIndication);
  };

  const handleNewOrder = async () => {
    if (!(await autoSave())) return;
    context.resetDraft();
    refs.resetState();
    navigate(PATHS.newOrder);
  };

  return (
    <>
      <PageLayout
        title={t("download.title")}
        subtitle={
          <>
            {approved ? t("download.approvedStatus") : t("download.reviewStatus")}
            {status !== "idle" && <> · <SaveStatus status={status} savedAt={savedAt} /></>}
          </>
        }
        onBack={handleBack}
        nav={false}
        action={
          approved && (
            <Button variant="outline" size="sm" icon={<LuFilePlus size={18} aria-hidden />} onClick={handleNewOrder} aria-label={t("download.newOrder")}>
              <span className="hidden min-[381px]:inline">{t("download.newOrder")}</span>
            </Button>
          )
        }
        cta={
          ready &&
          !approved && (
            <Button variant="primary" size="lg" icon={<LuCheck size={20} aria-hidden />} onClick={() => setApproved(true)}>
              {t("download.approve")}
            </Button>
          )
        }
      >
        {order.status === "streaming" && context.EditableResponse && <StreamedOrder text={context.EditableResponse} />}

        {order.status === "failed" && (
          <Card>
            <div role="alert" className="flex flex-col items-center gap-3 py-2 text-center">
              <p className="text-body text-fg-muted">{t("download.failed")}</p>
              <Button variant="outline" onClick={order.retry}>{t("common.retry")}</Button>
            </div>
          </Card>
        )}

        {order.status === "preparing" && (
          <>
            <div role="status" className="flex flex-col items-center gap-3 py-6 text-fg-muted">
              <Spinner size={40} />
              <p>{t("download.preparing")}</p>
            </div>
            <Card>
              {["w-[95%]", "w-[85%]", "w-[60%]"].map((w) => (
                <Skeleton key={w} className={`h-3.5 ${w}`} />
              ))}
            </Card>
          </>
        )}

        {ready && (
          <div className="stagger flex flex-col gap-4">
            {approved ? (
              <div className="flex items-center gap-3 rounded-card border border-line bg-success-soft px-4 py-3.5 animate-rise">
                <LuCircleCheck size={20} className="shrink-0 text-success" aria-hidden />
                <p className="text-body">
                  <strong>{t("download.approvedTitle")}</strong> {t("download.approvedBody")}
                </p>
              </div>
            ) : (
              <p className="-mt-1.5 text-caption text-fg-subtle">{t("download.editHint")}</p>
            )}

            <Card title={t("download.patient")} icon={<LuUser size={20} />}>
              <OrderField label={t("download.patientName")} field="patientName" />
              <OrderField label={t("download.patientDescription")} field="patientDescription" />
            </Card>
            <Card title={t("download.order")} icon={<LuFileText size={20} />}>
              <OrderField label={t("download.procedures")} field="procedures" readOnly />
              <OrderField label={t("download.cids")} field="cids" />
              <OrderField label={t("download.opmeSummary")} field="opmeSummary" />
            </Card>
            <Card title={t("download.schedule")} icon={<LuCalendar size={20} />}>
              <OrderField label={t("download.hospital")} field="hospital" />
              <OrderField label={t("download.desiredDate")} field="desiredDateTime" />
              <OrderField label={t("download.issueDate")} field="issueDate" readOnly />
            </Card>
          </div>
        )}

        {approved && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <Button variant="outline" size="sm" icon={<LuClipboardList size={18} aria-hidden />} loading={pdf.downloadResumo.loading} onClick={pdf.openResumoFlow}>
                {t("download.summary")}
              </Button>
              <Button variant="outline" size="sm" className="whitespace-nowrap" icon={<LuDownload size={18} aria-hidden />} onClick={() => setOtherGuides(true)}>
                {t("download.otherGuides")}
              </Button>
            </div>

            {approvedRefs.length > 0 && !pdf.showReferencesModal && (
              <ReferencesPanel
                count={approvedRefs.length}
                loading={pdf.downloadResumo.loading}
                onView={() => {
                  pdf.setReferencesModalInitialStep("validacao");
                  pdf.setShowReferencesModal(true);
                }}
                onRedownload={() => pdf.handleFinalDownloadResumo(approvedRefs)}
              />
            )}

            <h2 className="text-heading font-semibold text-fg-secondary">{t("download.share")}</h2>
            <div className="grid gap-2.5 sm:grid-cols-2">
              <ShareCard
                tone="mail"
                icon={<LuMail size={20} />}
                title={t("download.email")}
                description={t("download.emailDescription")}
                onClick={() => setEmailOpen(true)}
                highlight
              />
              <ShareCard
                tone="whatsapp"
                icon={<LuMessageCircle size={20} />}
                title={t("download.whatsapp")}
                description={t("download.whatsappDescription")}
                onClick={() => setWhatsAppOpen(true)}
                highlight
              />
            </div>
          </>
        )}
      </PageLayout>

      <Dialog open={otherGuides} onClose={() => setOtherGuides(false)} size="sm" title={t("download.otherGuidesTitle")}>
        <div className="flex flex-col gap-2.5">
          <Button
            variant="outline"
            fullWidth
            icon={<LuFileText size={18} aria-hidden />}
            onClick={() => {
              setOtherGuides(false);
              pdf.openGuiaInternacaoFlow();
            }}
          >
            {t("download.admissionGuide")}
          </Button>
          <Button
            variant="outline"
            fullWidth
            icon={<LuPackage size={18} aria-hidden />}
            onClick={() => {
              setOtherGuides(false);
              pdf.openOpmeSelection();
            }}
          >
            {t("download.opmeGuide")}
          </Button>
          <Button variant="outline" fullWidth icon={<LuUser size={18} aria-hidden />} onClick={() => navigate(PATHS.patientData)}>
            {t("download.patientData")}
          </Button>
        </div>
      </Dialog>

      {pdf.showDescricaoResumidaModal && (
        <SummaryDescriptionDialog
          suggestion={pdf.resumidaSuggestion}
          loading={pdf.generatingResumida}
          error={pdf.resumidaError}
          onRegenerate={pdf.generateDescricaoResumida}
          onConfirm={pdf.confirmDescricaoResumida}
          onClose={pdf.closeGuiaInternacaoFlow}
        />
      )}

      {pdf.showPlanSelection && (
        <HealthPlanPicker
          title={t("guides.admissionTitle")}
          plans={INTERNACAO_PLANS}
          loading={pdf.downloadInternacao.loading}
          onClose={() => pdf.setShowPlanSelection(false)}
          onDownload={pdf.handleDownloadGuiaInternacao}
          reportType="_pedido_de_cirurgia_resumido"
          secondStep
        />
      )}

      {pdf.showOpmeSelection && (
        <HealthPlanPicker
          title={t("guides.opmeTitle")}
          plans={OPME_PLANS}
          loading={pdf.downloadOpme.loading}
          onClose={() => pdf.setShowOpmeSelection(false)}
          onDownload={pdf.handleDownloadOPME}
          reportType="_opme_resumido"
        />
      )}

      {emailOpen && (
        <EmailComposer
          onClose={() => setEmailOpen(false)}
          patientName={data.patientName}
        />
      )}

      {whatsAppOpen && <WhatsAppComposer onClose={() => setWhatsAppOpen(false)} patientName={data.patientName} />}

      {pdf.showReferencesModal && (
        <ReferencesReviewDialog
          itensSelecionados={pdf.itensSelecionados}
          descricaoPaciente={data.patientDescription}
          onAprove={(selected) => pdf.handleFinalDownloadResumo(selected)}
          onSkip={() => pdf.handleFinalDownloadResumo()}
          onCancel={() => pdf.setShowReferencesModal(false)}
          state={refs}
          initialStep={pdf.referencesModalInitialStep}
        />
      )}
    </>
  );
};
